// React hooks
import { useCallback, useEffect, useState } from "react";

// Helpers i API servisi

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { TranslationSearchPageCmp, TranslationSearchPageProps } from "./types";
import {
  TRANSLATION_DELETE_PAGE_TITLE,
  TRANSLATION_EDIT_PAGE_TITLE,
  TRANSLATION_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";

// Lokalne komponente
import TranslationSearchResults from "./components/TranslationSearchResults/TranslationSearchResults";
import TranslationSearchFilters from "./components/TranslationSearchFilters/TranslationSearchFilters";

import { Modal } from "ui/components/Modal";




import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { useTranslate } from "helpers";
import { TranslationEventType, TranslationRow, TranslationSearchFormValues } from "services/api/translations/types";
import TranslationDeleteCard from "./components/TranslationDeleteCard/TranslationDeleteCard";
import { getTranslationsAPI } from "services/api/translations/api";
import TranslationEditCard from "./components/TranslationEditCard/TranslationEditCard";
import { initialTranslationSearchForm } from "services/api/translations";

const TranslationSearchPage: TranslationSearchPageCmp = (
  props: TranslationSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<TranslationRow>
  >(initialDataWithPagination);

  const [translationEventTypeId, setTranslationEventTypeId] =
    useState<string>("");
  const [translationId, setTranslationId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<TranslationSearchFormValues>(initialTranslationSearchForm);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination, filterValues.languageId);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    languageId?: string | null,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      if (languageId) {
        const results = await getTranslationsAPI(pagination, languageId, name);

        setSearchResults(results);
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setTranslationEventTypeId(TranslationEventType.EDIT);
    setTranslationId(id);
    setShowAddNewModal(true);
  }, []);

  const handleEventClick = useCallback(async (id: string, name: string) => {
    setTranslationId(id);
    setTranslationEventTypeId(name);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTranslationEventTypeId(TranslationEventType.ADD);
      setTranslationId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: TranslationSearchFormValues) => {
      setFilterValues(searchFormValues);

      fetchSearchResult(
        searchResults.pagination,
        searchFormValues.languageId,
        searchFormValues.search
      );
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination, filterValues.languageId, filterValues.search);
  };

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination, filterValues.languageId, filterValues.search);
    setShowAddNewModal(false);
  }, [filterValues]);

  const renderModalContent = () => {
    switch (translationEventTypeId) {
      case TranslationEventType.ADD:
        return (
          <>
            <ModalHeader
              title={t(TRANSLATION_EDIT_PAGE_TITLE.text)}
              onHide={() => setShowAddNewModal(false)}
              icon="/media/images/add-icon.svg"
            />
            <ModalBody>
              <TranslationEditCard
                languageId={filterValues.languageId}
                translationId=""
                onSuccess={handleEditSuccess}
                onClose={() => setShowAddNewModal(false)}
              />
            </ModalBody>
          </>
        );
      case TranslationEventType.EDIT:
        return (
          <>
            <ModalHeader
              title={t(TRANSLATION_EDIT_PAGE_TITLE.text)}
              onHide={() => setShowAddNewModal(false)}
              icon="/media/images/add-icon.svg"
            />
            <ModalBody>
              <TranslationEditCard
                languageId={filterValues.languageId}
                translationId={translationId}
                onSuccess={handleEditSuccess}
                onClose={() => setShowAddNewModal(false)}
              />
            </ModalBody>
          </>
        );
      case TranslationEventType.REMOVE:
        return (
          <>
            <ModalHeader
              title={t(TRANSLATION_DELETE_PAGE_TITLE.text)}
              onHide={() => setShowAddNewModal(false)}
            />
            <ModalBody>
              <TranslationDeleteCard
                translationId={translationId}
                onSuccess={handleEditSuccess}
              />
            </ModalBody>
          </>
        );
      default:
        return (
          <>
            <ModalHeader
              title={t("admin.nocontent.title")}
              onHide={() => setShowAddNewModal(false)}
            />
            <ModalBody>
              <p>{t("admin.nocontent.description")}</p>
            </ModalBody>
          </>
        );
    }
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border-b border-slate-200">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={TRANSLATION_SEARCH_PAGE_TITLE.text}
                  icon={TRANSLATION_SEARCH_PAGE_TITLE.icon}
                  iconColor={TRANSLATION_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  children={t("admin.translation.search.add")}
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <TranslationSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite prijevod klikom na 'Dodaj prijevod'" />
          ) : (
            <TranslationSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onEventClick={handleEventClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          {renderModalContent()}
        </Modal>
      )}
    </>
  );
};

TranslationSearchPage.route = Routes.TranslationSearchPageRouter;

export default TranslationSearchPage;
